import { notify } from '@billy/design-system'
import { ReactElement, ReactNode, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { EmberRoute } from '../../../enums/emberRoute'
import { NotificationKeys } from '../../../enums/notificationKeys'
import { useEmberRouteUrl } from '../../../hooks/routing/useEmberRouteUrl'
import { useOrganizationViewUrl } from '../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../utils/routing/reactRoute'
import { RouteElement } from '../../Route'
import { ProtectableComponentProps, ProtectedComponent } from '../ProtectedComponent'

const useOnRouteUnauthorized = (unauthorizedRedirectTo?: EmberRoute, onUnauthorized?: () => void) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { url: unauthorizedUrl } = useEmberRouteUrl(unauthorizedRedirectTo)
  const { url: dashboardUrl } = useOrganizationViewUrl(reactRoute.organization.dashboard.getMainRoute())

  return useCallback(() => {
    if (onUnauthorized) {
      onUnauthorized()
    } else {
      notify({ id: NotificationKeys.AccessDenied, message: t('access_denied'), variant: 'error' })
      const unauthorizedOrDashboardUrl = unauthorizedUrl || dashboardUrl
      if (unauthorizedOrDashboardUrl) {
        navigate(unauthorizedOrDashboardUrl)
      }
    }
  }, [dashboardUrl, navigate, onUnauthorized, t, unauthorizedUrl])
}

export interface RouteProtectedElementProps extends ProtectableComponentProps {
  children: ReactNode
  isHashRouter?: boolean
  unauthorizedRedirectTo?: EmberRoute
}

/*
 * Params bridge for use as the `element` of a genuine v6 <Route> (see RouteElement) that also
 * enforces the scope check.
 */
export const RouteProtectedElement = ({
  children,
  scopes,
  unauthorizedRedirectTo,
  onUnauthorized,
  unauthorizedNode,
  isHashRouter,
}: RouteProtectedElementProps): ReactElement => {
  const onRouteUnauthorized = useOnRouteUnauthorized(unauthorizedRedirectTo, onUnauthorized)

  return (
    <RouteElement isHashRouter={isHashRouter}>
      <ProtectedComponent scopes={scopes} unauthorizedNode={unauthorizedNode} onUnauthorized={onRouteUnauthorized}>
        {children}
      </ProtectedComponent>
    </RouteElement>
  )
}
